import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, TrendingUp, AlertTriangle, Target,
  BarChart3, Activity, Zap, DollarSign, Clock, ArrowRight,
  Bell
} from 'lucide-react';
import {
  AreaChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend
} from 'recharts';

const COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#6b7280',
  purple: '#7c3aed',
  green: '#10b981',
};

export default function ExecutiveDashboard() {
  const [stats, setStats] = useState<any>(null);
  const [scans, setScans] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const API = import.meta.env.VITE_API_URL || 'https://xfreescan-api.aforero.workers.dev/api';
  const headers = () => ({
    'Authorization': `Bearer ${localStorage.getItem('xfreescan_token') || ''}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [statsResp, scansResp, alertsResp] = await Promise.all([
        fetch(`${API}/dashboard/stats`, { headers: headers() }).then(r => r.json()).catch(() => ({})),
        fetch(`${API}/scans?limit=20`, { headers: headers() }).then(r => r.json()).catch(() => ({ data: [] })),
        fetch(`${API}/monitoring/alerts`, { headers: headers() }).then(r => r.json()).catch(() => ({ data: [] })),
      ]);
      setStats(statsResp.data || statsResp);
      setScans((scansResp.data || scansResp.scans || []).slice(0, 20));
      setAlerts(alertsResp.data || []);
    } catch {
      // silent fallback
    } finally {
      setIsLoading(false);
    }
  };

  // Build chart data from scan history
  const scoreTrend = scans
    .filter(s => s.status === 'completed')
    .reverse()
    .map(s => ({
      date: new Date(s.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
      score: s.score || 0,
      target: s.target,
    }));

  // Severity distribution from latest scan findings
  const latestScan = scans.find(s => s.status === 'completed');
  const severityData = latestScan ? [
    { name: 'Criticos', value: latestScan.critical || 0, color: COLORS.critical },
    { name: 'Altos', value: latestScan.high || 0, color: COLORS.high },
    { name: 'Medios', value: latestScan.medium || 0, color: COLORS.medium },
    { name: 'Bajos', value: latestScan.low || 0, color: COLORS.low },
  ].filter(d => d.value > 0) : [];

  // NIST radar data (mock from latest scan — in production would come from report)
  const nistData = [
    { function: 'GV', fullName: 'Gobernar', score: 35 },
    { function: 'ID', fullName: 'Identificar', score: 60 },
    { function: 'PR', fullName: 'Proteger', score: 45 },
    { function: 'DE', fullName: 'Detectar', score: 55 },
    { function: 'RS', fullName: 'Responder', score: 30 },
    { function: 'RC', fullName: 'Recuperar', score: 25 },
  ];

  const unreadAlerts = alerts.filter(a => !a.is_read).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-purple-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security Cockpit</h1>
          <p className="text-sm text-gray-500">Postura de seguridad en tiempo real</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/app/alerts" className="relative inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Bell className="h-4 w-4" />
            Alertas
            {unreadAlerts > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unreadAlerts}
              </span>
            )}
          </Link>
          <Link to="/app/scans/new" className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700">
            <Zap className="h-4 w-4" /> Nuevo Scan
          </Link>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard label="Score Actual" value={`${latestScan?.score || '—'}/100`} icon={Shield} color="purple" />
        <KpiCard label="Calificacion" value={latestScan?.grade || '—'} icon={Target} color={latestScan?.grade?.startsWith('A') ? 'green' : latestScan?.grade?.startsWith('F') ? 'red' : 'yellow'} />
        <KpiCard label="Escaneos" value={stats?.total_scans || scans.length} icon={Activity} color="blue" />
        <KpiCard label="Hallazgos" value={stats?.total_findings || '—'} icon={AlertTriangle} color="orange" />
        <KpiCard label="Criticos Abiertos" value={latestScan?.critical || 0} icon={Zap} color="red" />
        <KpiCard label="Alertas Activas" value={unreadAlerts} icon={Bell} color={unreadAlerts > 0 ? 'red' : 'green'} />
      </div>

      {/* Charts Row 1: Score Trend + Severity Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Score Trend */}
        <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-purple-600" />
            Tendencia de Score
          </h3>
          {scoreTrend.length > 1 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={scoreTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Area type="monotone" dataKey="score" stroke="#7c3aed" fill="url(#purpleGradient)" strokeWidth={2} />
                <defs>
                  <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-sm text-gray-400">
              Necesita al menos 2 escaneos para mostrar tendencia
            </div>
          )}
        </div>

        {/* Severity Donut */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-orange-500" />
            Distribucion de Hallazgos
          </h3>
          {severityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={severityData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                  {severityData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-sm text-gray-400">
              Sin datos de hallazgos
            </div>
          )}
        </div>
      </div>

      {/* Charts Row 2: NIST Radar + Recent Scans */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* NIST Radar */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-600" />
            Madurez NIST CSF 2.0
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={nistData}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="function" tick={{ fontSize: 10, fontWeight: 700 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 8 }} />
              <Radar name="Score" dataKey="score" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.3} strokeWidth={2} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Scans */}
        <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            Escaneos Recientes
          </h3>
          <div className="space-y-2">
            {scans.filter(s => s.status === 'completed').slice(0, 5).map((scan) => (
              <Link key={scan.id} to={`/app/reports/${scan.id}`}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-purple-200 hover:bg-purple-50/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-xs font-black text-white ${
                    scan.grade?.startsWith('A') ? 'bg-emerald-500' :
                    scan.grade?.startsWith('B') ? 'bg-green-500' :
                    scan.grade?.startsWith('C') ? 'bg-yellow-500' :
                    scan.grade?.startsWith('D') ? 'bg-orange-500' : 'bg-red-500'
                  }`}>
                    {scan.grade || '?'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{scan.target}</p>
                    <p className="text-[10px] text-gray-500">
                      {new Date(scan.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-gray-700">{scan.score || 0}/100</span>
                  {scan.critical > 0 && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">{scan.critical} crit</span>}
                  <ArrowRight className="h-4 w-4 text-gray-300" />
                </div>
              </Link>
            ))}
            {scans.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">No hay escaneos completados</p>
            )}
          </div>
        </div>
      </div>

      {/* Financial Risk Summary */}
      <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-gray-900 to-gray-800 p-6 shadow-sm">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-green-400" />
          Riesgo Financiero Estimado
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Prob. Brecha 12m', value: latestScan?.score >= 70 ? '15%' : latestScan?.score >= 50 ? '30%' : '50%+', color: 'text-red-400' },
            { label: 'Costo Est. Brecha', value: '$2.76M', color: 'text-orange-400' },
            { label: 'Perdida Anual Esp.', value: latestScan?.score >= 70 ? '$414K' : latestScan?.score >= 50 ? '$828K' : '$1.38M', color: 'text-yellow-400' },
            { label: 'Prima Est. Seguro', value: latestScan?.score >= 70 ? '$6-10K' : latestScan?.score >= 50 ? '$15-25K' : '$30-50K', color: 'text-green-400' },
          ].map((metric, i) => (
            <div key={i} className="text-center">
              <p className={`text-xl font-black ${metric.color}`}>{metric.value}</p>
              <p className="text-[10px] text-gray-400 font-medium mt-1">{metric.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Scorecard */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="h-4 w-4 text-green-600" />
          Compliance Scorecard
        </h3>
        <div className="space-y-3">
          {[
            { framework: 'NIST CSF 2.0', coverage: 42 },
            { framework: 'OWASP Top 10', coverage: 65 },
            { framework: 'PCI-DSS v4.0', coverage: 38 },
            { framework: 'ISO 27001:2022', coverage: 30 },
            { framework: 'Ley 1581 (Habeas Data)', coverage: 55 },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="text-xs font-medium text-gray-700 w-40 shrink-0">{item.framework}</span>
              <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    item.coverage >= 70 ? 'bg-emerald-500' :
                    item.coverage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${item.coverage}%` }}
                />
              </div>
              <span className="text-xs font-bold text-gray-600 w-10 text-right">{item.coverage}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Helper Components ----

function KpiCard({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: any; color: string }) {
  const colorMap: Record<string, string> = {
    purple: 'text-purple-500',
    green: 'text-emerald-500',
    red: 'text-red-500',
    yellow: 'text-yellow-500',
    blue: 'text-blue-500',
    orange: 'text-orange-500',
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`h-5 w-5 ${colorMap[color] || 'text-gray-500'}`} />
      </div>
      <p className="text-2xl font-black text-gray-900">{value}</p>
      <p className="text-[10px] text-gray-500 font-medium uppercase mt-1">{label}</p>
    </div>
  );
}
